import { isEmpty } from 'lodash';

import {
  CompletionTipType,
  IConditionFlow,
  IMission,
  IScene,
  ISceneFlow,
  IStory,
  SceneType,
} from '@/interface';
import { IRuleNum } from '@/interface/gamePlay';
import { endAndMoreOptions } from '@/pages/mission/info/card/event/customBranch/SelectBranch';

import { NodeEnums } from '../const/node';
import { ConditionType } from '../nodes/condition/const';
import { AppNode } from '../types';
import { nodeCanDelete } from './disabled';
import { getSceneBots } from './exclSceneToNodes';
import { getNodeId } from './getNodeId';

// 处理列表项并添加 canDelete 属性
const processList = <T extends { id: string }>(
  list: T[],
  canDeleteFunc: (item: T) => boolean,
) => list.map(item => ({ ...item, canDelete: canDeleteFunc(item) }));

// 更新或添加项目到列表
const updateOrAddToList = <T extends { id: string }>(
  list: T[],
  item: T,
): T[] => {
  const index = list.findIndex(i => i.id === item.id);
  if (index === -1) {
    return [...list, item];
  }
  return [...list.slice(0, index), item, ...list.slice(index + 1)];
};

// 幕节点数据处理，幕表单->幕节点
const confirmScene = (postData: IScene) => {
  const isAI = postData?.sceneType !== SceneType.SCENE_TYPE_OPTION;
  const sceneFlow: ISceneFlow = {
    id: postData?.id || '',
    storyId: postData.storyId || '',
    ...postData,
    bots: getSceneBots(postData),
  };

  const draft = !(
    sceneFlow.name?.trim()
    && (isAI ? sceneFlow.goal?.trim() : true)
    && sceneFlow.image?.url?.url
    && sceneFlow.bots
    && sceneFlow.bots.length > 0
  );

  let gamePlayCountDownDraft = false;
  if (postData?.aiGamePlay?.countDown?.isOpen && (postData?.aiGamePlay?.countDown?.val <= 0 || !postData?.aiGamePlay?.countDown?.val)) {
    // 倒计时应大于0
    gamePlayCountDownDraft = true;
  }


  let gamePlayNumSysDownDraft = false;
  if (postData?.aiGamePlay?.numSys?.isOpen) {
    const rules = postData?.aiGamePlay?.numSys?.rules || [];
    if (rules.length === 0) {
      gamePlayNumSysDownDraft = true;
    }

    const wrong = rules?.map((e: IRuleNum) => {
      if (!e?.numId || !e?.rule?.trim()) {
        return true;
      }
      return false;
    }).some(((e: boolean) => e === true));
    if (wrong) {
      gamePlayNumSysDownDraft = true;
    }
  }

  const isDraft = draft || gamePlayNumSysDownDraft || gamePlayCountDownDraft || sceneFlow?.extendMission?.isDraft;

  return {
    ...sceneFlow,
    isDraft,
    canDelete: nodeCanDelete(postData),
  };
};

const confirmMission = (
  postData: IMission,
  type?: NodeEnums,
  params?: {
    missionInfo?: {
      list: IMission[];
    };
    data: AppNode['data'];
  },
) => {
  if (!postData?.id) {
    return;
  }
  const list = params?.missionInfo?.list || [];
  const updatedList = updateOrAddToList(list, postData);
  const newData = {
    ...params?.data.info,
    list: processList(updatedList, nodeCanDelete),
  };
  if (type === NodeEnums.mainMission) {
    return { ...newData };
  }

  return {
    ...postData,
    id: postData?.id,
    isDraft: postData?.isDraft,
    canDelete: nodeCanDelete(postData),
  };
};

const confirmProps = (
  postData: IMission,
  params?: {
    propsInfo?: {
      list?: IMission[];
    };
    data: AppNode['data'];
  },
) => {
  if (!postData?.id) {
    return {};
  }
  const list = params?.propsInfo?.list || [];
  const updatedList = updateOrAddToList(list, postData);
  return {
    propsInfo: {
      list: processList(updatedList, nodeCanDelete),
    },
  };
};

const confirmStory = (postData: IStory) => {
  const storyFlow = {
    id: postData?.id || '',
    name: postData.name,
    space: postData?.spaceDetail?.name,
    intro: postData.intro,
    image: postData.image,
    bots: postData.botPromptList,
    playerInfo: postData.playerInfo,
    status: postData?.status,
    detailStatus: postData?.detailStatus,
  };
  const draft = !(
    storyFlow.name?.trim()
    && storyFlow.intro?.trim()
    && storyFlow.image?.url?.url
  );

  return {
    ...storyFlow,
    isDraft: draft,
    canDelete: nodeCanDelete(postData),
  };
};

const confirmCondition = (
  postData: any,
  index: number,
  sceneId: string,
  rootId: string,
  missionMap: Map<string, string>,
  canDelete: boolean,
  sceneType: SceneType,
) => {
  const mId = missionMap.has(postData.triggerMissionId)
    ? postData.triggerMissionId
    : '';
  const type = postData.passCondType;
  const missions = postData.specifyMission?.missions || [];

  const condition: IConditionFlow = {
    id: postData.condId || `condition_${getNodeId(index)}`,
    conditionNodeId: '',
    sceneId,
    rootId,
    type,
    canDelete,
    missions: missions.map((m: any) => ({
      id: m.missionId,
      name: sceneType === SceneType.SCENE_TYPE_OPTION
        ? m.missionName
        : missionMap.get(m.missionId) || m.missionName,
    })),
    openTrigger: !!postData.triggerTrap?.specifyNumeric?.isUsed,
    openCount: !!postData.triggerTrap?.countDown,
    triggerValue: {
      minV: postData.triggerTrap?.specifyNumeric?.minVal,
      maxV: postData.triggerTrap?.specifyNumeric?.maxVal,
    },
    countDown: postData.triggerTrap?.countDown,
    triggerMissionId: mId,
    nextSceneId: postData.nextSceneId,
    isEmpty: false,
    isDraft:
      sceneType !== SceneType.SCENE_TYPE_OPTION
      && ((type === ConditionType.PASS && missions.length === 0)
        || (type === ConditionType.TRAP && !postData.triggerTrap)),
  };
  return condition;
};

// 结局幕或选项幕的jump节点info
const confirmFinalJump = (node: AppNode, scene: IScene) => {
  const tipType = scene?.extendMission?.completionTip
    ?.tipType as CompletionTipType;
  const jumpInfo = endAndMoreOptions?.filter(e => e.enum === tipType)?.[0];
  const jumpTempInfo = { nextScene: { id: '', name: jumpInfo?.label } };
  const info = isEmpty(jumpInfo) ? node?.data?.info : jumpTempInfo;
  return info;
};

export {
  confirmCondition,
  confirmFinalJump,
  confirmMission,
  confirmProps,
  confirmScene,
  confirmStory,
};

